import {
  AppointmentTypeSettingDTO,
} from 'dtos/appointmentTypeSettingDTO';
import compact from 'lodash/compact';
import map from 'lodash/map';
import startCase from 'lodash/startCase';
import zipObject from 'lodash/zipObject';
import {
  visitTypeColors,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  isEmptyString,
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import {
  AppointmentTypes,
  AppointmentTypeSettings,
} from 'views/EMR/Settings/Appointments/types';
import pick from 'lodash/pick';
import {
  BillingProviderRuleDTO,
  ClaimInsuranceTypeSettingDTO,
  NpiDTO,
  PayerEntryConfigDTO,
  ServiceFacilityDTO,
} from 'dtos';
import {
  paperPayerKeys,
} from 'views/EMR/Settings/Payers/PaperPayerForm';
import values from 'lodash/values';
import {
  BillingProviderRuleForm,
} from 'views/EMR/Settings/BillingConfigurations/BillingProviderRules/Dialog';
import isNil from 'lodash/isNil';
import DOMPurify from 'dompurify';

export function assignAppointmentTypeColors(
  appointmentTypes: string[],
): Record<string, string> {
  return {
    ...zipObject(
      appointmentTypes,
      appointmentTypes.map((type, index) => visitTypeColors[index % appointmentTypes.length]),
    ),
    'Time Blocked': '!bg-rose-300',
  };
}

export function normalizeAppointmentTypeSettings(
  data: AppointmentTypeSettingDTO[],
): AppointmentTypeSettings[] {
  const appointmentTypes = data?.filter((item) => item?.appointmentTypeName !== 'Time Blocked');
  const colors = assignAppointmentTypeColors(
    compact(map(appointmentTypes, 'appointmentTypeName')),
  );

  return appointmentTypes?.map((item) => ({
    ...item,
    color: colors[item?.appointmentTypeName ?? ''],
  }));
}

export function normalizeAppointmentTypes(
  data: AppointmentTypeSettingDTO[],
): AppointmentTypes {
  const appointmentTypes = compact(map(data, 'appointmentTypeName')).filter((appointmentType) => appointmentType !== 'Time Blocked');
  const durationByType = mapValues(
    keyBy(data, 'appointmentTypeName'),
    (item) => item?.defaultDurationInMinutes ?? 15,
  );
  const isTelehealthByType = mapValues(
    keyBy(data, 'appointmentTypeName'),
    (item) => item?.isTelehealth ?? false,
  );

  return { appointmentTypes, durationByType, isTelehealthByType };
}

export function getSectionLabelByName(sectionName: string = '') {
  return startCase(sectionName?.replace('Template', ''));
}

export const EMPTY_CKE_TEXT = '<p><br data-cke-filler="true"></p>';
const FIELD_PATTERN = /\$\("([\w|\s|\-|_|?|/|:|.|\\]+)":(\d+)([[|\]|"|\w|\s|_|\-|,|?|\\|/|:|.]*)\)/gi;
const FIELD_SPAN_PATTERN = /<span\s+class="__populate__-field"[^>]*data-field-selection="([^"]*)"[^>]*>.*?<\/span>/g;

export function isEmptyRichText(content: string = '') {
  return content === EMPTY_CKE_TEXT;
}

function replaceWithField(match: string, title: string, id: string, selection: string) {
  const selected = JSON.parse(selection ?? '[]');
  return `<span class="__populate__-field" data-field-id="${id}" data-field-title="${title}" data-field-selection="${selection.replaceAll('"', '&quot;')}">${title}${selected.length > 0 ? ` [${selected.join(', ')}]` : ''}</span>`;
}

function replaceWithFieldValues(match: string, title: string, id: string, selection: string) {
  const selected = JSON.parse(selection ?? '[]');
  return (selected ?? []).join(', ');
}

export function normalizeTextWithFields(content: string = '') {
  return content.replace(FIELD_PATTERN, replaceWithFieldValues);
}

/* Remove field labels from html content, to show in Encounter->Past visits plan */
export function removeFieldLabelsFromHtml(htmlContent: string = ''): string {
  return isEmptyString(htmlContent)
    ? htmlContent
    : htmlContent.replace(FIELD_SPAN_PATTERN, (match, selection) => {
      try {
        const decodedSelection = selection.replace(/&quot;/g, '"');
        const selectedValues = JSON.parse(decodedSelection);
        return `<span>${selectedValues.join(', ')}</span>`;
      } catch (error) {
        console.warn('Failed to parse field selection:', selection, error);
        return match;
      }
    });
}

function isHtmlContent(content: string): boolean {
  // Check for common HTML tags
  const htmlTagPattern = /<[^>]+>/;
  return htmlTagPattern.test(content);
}

// for backwards compatibility
function normalizePlainText(paragraph: string, keepFieldLabel: boolean): string {
  return isEmptyString(paragraph)
    ? '<p>&nbsp;</p>'
    : `<p>${paragraph.replace(FIELD_PATTERN, keepFieldLabel
      ? replaceWithField // for visit note
      : replaceWithFieldValues)}</p>`; // for readonly details on encounters
}

export function normalizeContent(content: string = '', keepFieldLabel: boolean = true) {
  const isPlainText = notEmptyString(content) && !isHtmlContent(content);

  const result = isPlainText
    ? (content ?? '').toString().trim()
      .split(/\r?\n/)
      .map((paragraph) => normalizePlainText(paragraph, keepFieldLabel))
      .join('')
    : DOMPurify.sanitize(content);

  const canonical = result
    .replace(/\u00A0/g, '&nbsp;') // NBSP char -> &nbsp;
    .replace(/ {2,}/g, (m) => '&nbsp;'.repeat(m.length)); // preserve runs

  return isEmptyString(content)
    ? EMPTY_CKE_TEXT
    : canonical;
}

export function normalizeContentForTextarea(content: string = '') {
  const isPlainText = notEmptyString(content) && !isHtmlContent(content);

  const result = isPlainText
    ? (content ?? '')
      .split('\n')
      .map((paragraph) => (isEmptyString(paragraph)
        ? '<p>&nbsp;</p>'
        : `<p>${paragraph}</p>`)).join('')
    : DOMPurify.sanitize(content);

  return result;
}

export function serializeContent(htmlContent: string = '') {
  return isEmptyString(htmlContent)
    ? null
    : htmlContent;
}

export function normalizePaperPayerFormData(
  { isPinned, ...data }: PayerEntryConfigDTO,
  claimInsuranceTypeSettings: ClaimInsuranceTypeSettingDTO[],
) {
  return {
    ...pick(data?.payerEntry, paperPayerKeys),
    isPinned,
    insuranceType: claimInsuranceTypeSettings?.find(
      (setting) => setting.insuranceType === data?.payerEntry?.insuranceType,
    ),

  };
}

export function validateRequiredNPI(value: NpiDTO) {
  const { number, taxonomyCode, type } = value ?? {};
  const allFilled = (number ?? '').replaceAll('_', '').length === 10
    && (taxonomyCode ?? '').replaceAll('_', '').length === 10
    && notEmptyString(type);

  return allFilled || 'Provide valid NPI Information';
}

export function validateNPI(value: NpiDTO) {
  const allEmpty = values(value).every(isEmptyString);
  const allFilled = validateRequiredNPI(value);

  return allEmpty || allFilled || 'Provide valid NPI Information';
}

export function serializeServiceFacility(data: ServiceFacilityDTO) {
  const npi = values(data?.npi ?? {}).every(isEmptyString) ? undefined : data?.npi;
  return {
    ...data,
    address: pick(data?.address, ['street', 'unit', 'city', 'state', 'zipCode']),
    npi: data.isInternal ? undefined : npi,
  };
}

export function serializeBillingProviderRules({
  billingProviderRuleId,
  organizationId,
  order,
  payer,
  renderingProvider,
  serviceFacility,
  billingProvider,
}: BillingProviderRuleForm): BillingProviderRuleDTO {
  return {
    order,
    billingProviderRuleId,
    organizationId,
    payerEntryId: isNil(payer) ? null : payer?.payerEntryId,
    renderingProviderId: isNilOrZero(renderingProvider?.renderingProviderId)
      ? undefined
      : renderingProvider?.renderingProviderId,
    serviceFacilityId: isNilOrZero(serviceFacility?.serviceFacilityId)
      ? null
      : serviceFacility?.serviceFacilityId,
    // 0 is the "Not Set" option.
    billingProviderId: isNilOrZero(billingProvider?.billingProviderId)
      ? null
      : billingProvider?.billingProviderId,
  };
}
